import { useLocale } from '@/i18n/locale'

/**
 * Все числа здесь круглые и заведомо иллюстративные, а метка «данные
 * условные» стоит на самом макете: экран не должен читаться как результат
 * клиента (ТЗ 3A.1 п. 3). Менять содержание — не подставлять ничего, что
 * похоже на реальный кейс.
 */
const COPY = {
  ru: {
    aria: 'Пример экрана, данные условные: окно собственника. Слева лента из трёх наблюдений ИИ с предложенным действием, справа три показателя — маржа, выручка на сотрудника и доля задач через ИИ.',
    title: 'окно собственника',
    sample: 'Пример экрана · данные условные',
    feed: [
      ['Половину просроченной дебиторки дают три контрагента.', 'Поставить на контроль', '09:40'],
      ['Договор согласуется пять дней, четыре из них — ждёт в очереди.', 'Сократить цепочку', '11:15'],
      ['Повторные заказы падают там, где клиенту не звонили дольше месяца.', 'Собрать список на обзвон', '14:30'],
    ],
    ask: 'спросить что угодно',
    metrics: [
      { label: 'маржа', value: '20', unit: '%', delta: '+2 процентных пункта за квартал' },
      { label: 'выручка на сотрудника', value: '500', unit: 'тыс. ₽/мес', delta: '+50 тыс. ₽ за квартал' },
      { label: 'доля задач через ИИ', value: '40', unit: '%', delta: '+10 процентных пунктов за месяц' },
    ],
  },
  en: {
    aria: 'Sample screen, illustrative data: the owner’s window. On the left, a feed of three AI observations with a suggested action; on the right, three figures — margin, revenue per employee and share of tasks done through AI.',
    title: 'owner’s window',
    sample: 'Sample screen · illustrative data',
    feed: [
      ['Three counterparties account for half of overdue receivables.', 'Put under watch', '09:40'],
      ['A contract takes five days to approve; four of them it waits in a queue.', 'Shorten the chain', '11:15'],
      ['Repeat orders drop where the client got no call for over a month.', 'Build a call list', '14:30'],
    ],
    ask: 'ask anything',
    metrics: [
      { label: 'margin', value: '20', unit: '%', delta: '+2 percentage points over the quarter' },
      { label: 'revenue per employee', value: '$6K', unit: '/mo', delta: '+$500 over the quarter' },
      { label: 'share of tasks via AI', value: '40', unit: '%', delta: '+10 percentage points over the month' },
    ],
  },
}

export function OwnerWindowMock() {
  const t = COPY[useLocale()]
  return (
    <div
      role="img"
      aria-label={t.aria}
      className="hiw-mock rounded-card border border-divider bg-card font-mono text-ink"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-divider px-4 py-3 sm:px-5">
        <div className="text-[0.9375rem] leading-tight">{t.title}</div>
        <span className="hiw-chip hiw-chip--sample">{t.sample}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="p-3 sm:p-4">
          <div className="flex h-full flex-col rounded-[10px] border border-divider bg-bg">
            <ul className="flex-1 divide-y divide-divider">
              {t.feed.map(([text, action, time]) => (
                <li key={time} className="px-4 py-3 sm:px-5 sm:py-4">
                  <p className="text-[0.8125rem] leading-relaxed sm:text-sm">{text}</p>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="hiw-chip hiw-chip--action">
                      <span className="hiw-dot" />
                      {action}
                    </span>
                    <span className="text-[0.6875rem] text-muted">{time}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-3 border-t border-divider px-4 py-3 text-[0.8125rem] text-muted sm:px-5">
              <span>
                {t.ask}
                <span className="hiw-caret" />
              </span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
                <path d="M2 7h10M8 3l4 4-4 4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex flex-col px-4 pb-4 sm:px-5 md:py-4 md:pl-2">
          {t.metrics.map((m) => (
            <div
              key={m.label}
              className="flex flex-1 items-end justify-between gap-3 border-t border-ink/70 py-2.5 md:flex-col md:items-start md:justify-start md:gap-0 md:py-4"
            >
              <div className="min-w-0 md:contents">
                <div className="text-[0.8125rem] leading-tight">{m.label}</div>
                <div className="mt-1 text-[0.6875rem] leading-snug text-muted md:order-3 md:mt-2">{m.delta}</div>
              </div>
              <div className="flex shrink-0 items-start gap-1 md:order-2 md:mt-2">
                <span className="hiw-metric">{m.value}</span>
                <span className="mt-0.5 text-[0.8125rem] text-muted md:mt-1 md:text-base">{m.unit}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
